// PrintHQ Cloud's account state: the server's entitlement (rendered here,
// never decided here), the feature flags, the Maker prices and the in-app
// notifications. Loaded once after sign-in; refreshed on focus and after
// Checkout. Until the Cloud migrations are applied the flags read as off,
// so the previous screens keep showing.
import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { cloud, type CloudNotification, type Entitlement, type Flags, type Plans } from '@/services/cloud'

/** What each flag means when the server can't say (everything new off). */
const DEFAULT_FLAGS: Flags = {
  cloud_dashboard: false,
  cloud_pricing_page: false,
  trial_eligibility: false,
  trial_activation: false,
  vault_trial_enforcement: false,
  legacy_nav: true,
  storage_entitlements: false,
  planner: false,
  planner_google: false,
  planner_feeds: false,
  planner_alerts: false,
}

export const useCloudStore = defineStore('cloud', () => {
  const entitlement = ref<Entitlement | null>(null)
  const flags = ref<Flags>({ ...DEFAULT_FLAGS })
  const plans = ref<Plans | null>(null)
  const notifications = ref<CloudNotification[]>([])
  const loading = ref(false)
  const error = ref<string | null>(null)
  const loadedAt = ref(0)
  /** The first load finished (well or not): the screens can choose. */
  const ready = ref(false)

  const flag = (k: string) => flags.value[k] ?? DEFAULT_FLAGS[k] ?? false
  const state = computed(() => entitlement.value?.state ?? null)
  const unread = computed(() => notifications.value.filter((n) => !n.read_at).length)

  async function load(opts: { refresh?: boolean } = {}) {
    loading.value = true
    error.value = null
    try {
      const r = await cloud.entitlement(opts.refresh)
      entitlement.value = r.entitlement
      flags.value = { ...DEFAULT_FLAGS, ...r.flags }
      loadedAt.value = Date.now()
    } catch (e) {
      error.value = (e as Error).message
      // The flags still matter (they choose the screens): ask for them alone.
      try {
        flags.value = { ...DEFAULT_FLAGS, ...(await cloud.flags()) }
      } catch {
        flags.value = { ...DEFAULT_FLAGS }
      }
    } finally {
      loading.value = false
      ready.value = true
    }
  }

  async function loadPlans() {
    if (plans.value) return plans.value
    try {
      plans.value = await cloud.plans()
    } catch {
      plans.value = null
    }
    return plans.value
  }

  async function loadNotifications() {
    try {
      notifications.value = await cloud.notifications()
    } catch {
      /* before the migrations: no notifications */
    }
  }

  async function markAllRead() {
    const ids = notifications.value.filter((n) => !n.read_at).map((n) => n.id)
    await cloud.markRead(ids).catch(() => {})
    const now = new Date().toISOString()
    notifications.value = notifications.value.map((n) => (n.read_at ? n : { ...n, read_at: now }))
  }

  async function startTrial() {
    const r = await cloud.startTrial()
    entitlement.value = r.entitlement
    return r
  }

  /** Back at the tab (after Checkout in another one): re-read, at most every 20 s. */
  function onFocus() {
    if (Date.now() - loadedAt.value > 20_000) load({ refresh: !entitlement.value?.entitled }).catch(() => {})
  }

  return { entitlement, flags, plans, notifications, loading, error, ready, state, unread, flag, load, loadPlans, loadNotifications, markAllRead, startTrial, onFocus }
})
